import { useEffect, useCallback } from "react";
import { Store } from "../types/StoreType";
import * as persistenceManager from "../services/persistenceManager";
import { reloadPage } from "../services/browserUtils";

/**
 * Coles Project IDs - users belonging to these projects should be redirected to the
 * custom Coles Hello Screens environment.
 *
 * This is a temporary fix to prevent dual-environment access which is causing:
 * - Orders being updated across both environments
 * - Incomplete and inaccurate wait time analytics at stores
 * - Operational issues at store level
 *
 * Set via environment variable REACT_APP_COLES_PROJECT_IDS as comma-separated values.
 * Example: "6f11567c-a672-487f-8586-130a8fcdb65c,147cd87f-ebd4-4cd9-bf4c-2c78a1da58d2"
 *
 * TODO: Remove this once the Coles migration is complete
 */
const getColesProjectIds = (): string[] => {
  const envValue = process.env.REACT_APP_COLES_PROJECT_IDS || "";
  return envValue
    .split(",")
    .map((id) => id.trim())
    .filter((id) => id.length > 0);
};

/**
 * Custom hook that detects users belonging to the Coles project and forces
 * them to log out. This ensures they are redirected to the correct custom
 * Coles Hello Screens environment on their next login.
 *
 * @param store - The store information for the current logged-in user
 * @param onClearOrders - Function to clear orders from the OrderStatusContext
 */
export const useColesProjectLogout = (
  store: Store | undefined,
  onClearOrders: () => void
): void => {
  const forceLogout = useCallback(() => {
    console.log(
      "Coles project detected. Forcing logout to redirect user to custom Coles Hello Screens."
    );
    persistenceManager.deleteAllValues();
    onClearOrders();
    // Reload the page to trigger the login flow, which will redirect to the correct environment
    reloadPage();
  }, [onClearOrders]);

  useEffect(() => {
    const colesProjectIds = getColesProjectIds();
    if (store?.projectId && colesProjectIds.includes(store.projectId)) {
      forceLogout();
    }
  }, [store, forceLogout]);
};

export default useColesProjectLogout;
